import { motion } from "framer-motion";
import { Brain, Activity, ShieldOff, Compass, Target } from "lucide-react";

const BENEFITS = [
  {
    code: "01",
    title: "Entender por qué haces lo que haces",
    desc: "Decodifica las creencias y experiencias que escribieron tu sistema operativo mental.",
    Icon: Brain,
    span: "lg:col-span-7 lg:row-span-2",
    feature: true,
  },
  {
    code: "02",
    title: "Romper patrones de auto-sabotaje",
    desc: "Identifica los bucles y desactívalos con protocolos repetibles.",
    Icon: Activity,
    span: "lg:col-span-5",
  },
  {
    code: "03",
    title: "Reducir la ansiedad de fondo",
    desc: "Salir del piloto automático que vive en estado de alerta permanente.",
    Icon: ShieldOff,
    span: "lg:col-span-5",
  },
  {
    code: "04",
    title: "Decidir con claridad",
    desc: "Sin bloqueo, sin parálisis. Cada decisión alineada con quien quieres ser.",
    Icon: Compass,
    span: "lg:col-span-6",
  },
  {
    code: "05",
    title: "Actuar alineado con lo que necesitas",
    desc: "No es un curso más. Es un espacio donde dejas de repetir y empiezas a crear.",
    Icon: Target,
    span: "lg:col-span-6",
  },
];

export default function Solution() {
  return (
    <section
      id="solucion"
      data-testid="solution-section"
      className="surface-dark relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="warm-halo" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-10 bg-pearl/40" />
              <span className="font-body text-[11px] tracking-[0.28em] uppercase text-pearl/70">
                02 · Sistema
              </span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              data-testid="solution-headline"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-normal tracking-[-0.02em] leading-[1.0] text-cream"
            >
              Cinco protocolos
              <br />
              para reescribir
              <br />
              <em className="italic text-pearl">tu código mental.</em>
            </motion.h2>
          </div>
          <div className="lg:col-span-5 lg:pt-10">
            <p className="font-body text-base sm:text-lg text-pearl/80 leading-relaxed">
              SIRCS no es un lugar donde consumes contenido. Es un sistema operativo de
              transformación interna que combina psicología, neurociencia aplicada y disciplina
              diaria dentro de una comunidad privada.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:grid-rows-2 gap-4 lg:gap-5">
          {BENEFITS.map((b, i) => (
            <motion.article
              key={b.code}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className={`relative bg-[#121820] rounded-2xl border border-[#1E252F] p-8 lg:p-10 ${b.span} group hover:border-warm/40 transition-colors min-h-[240px] flex flex-col justify-between overflow-hidden`}
              data-testid={`solution-benefit-${i}`}
            >
              {b.feature && (
                <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-30 pointer-events-none"
                  style={{ background: "radial-gradient(circle, rgba(201,168,106,0.35), transparent 70%)" }}
                />
              )}
              <div className="relative">
                <div className="flex items-center justify-between mb-8">
                  <span className="font-body text-[10px] tracking-[0.28em] uppercase text-warm/80">
                    Protocolo · {b.code}
                  </span>
                  <b.Icon size={20} strokeWidth={1.3} className="text-pearl/70 group-hover:text-warm transition-colors" />
                </div>
                <h3 className={`font-display font-normal tracking-[-0.01em] leading-[1.1] text-cream ${b.feature ? "text-3xl sm:text-4xl lg:text-5xl" : "text-2xl sm:text-3xl"}`}>
                  {b.title}
                </h3>
              </div>
              <p className="relative mt-6 font-body text-pearl/70 text-sm sm:text-base leading-relaxed max-w-md">
                {b.desc}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
